import { flatten, intersection, isNil, mapValues, omit } from "lodash";
import { keys } from "ramda";

import { UpstreamItemStatus } from "../../../commons/libs/externals/veloconnect-proxy";
import { byDefined } from "../../../commons/libs/filter-guards";
import { BicycleSpecKey, BicycleTypeKey, CustomBicycleFilterKey } from "../../../commons/specs/bicycle";
import {
  ActiveFilters,
  FilterConfig,
  FilterConfigItem,
  FilterConfigMeta,
  FilterKey,
  FilterValue,
  getFilterKeysForFilterItem,
  ProductFilter,
  ProductFilterValues
} from "../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../commons/specs/product";
import { AssortmentType } from "../../../commons/types/assortment";
import {
  ManualAssortmentAvailabilityState,
  WawiAssortmentAvailabilityState,
  type CombinedAssortmentAvailabilityState,
  type PrefixedUpstreamItemStatus,
  type PrefixedWawiAssortmentAvailabilityState
} from "../../../commons/types/availability";
import { Brand, InsufficientFilterValues } from "../../../commons/types/brand";
import {
  AutomaticAssortmentFilter,
  AvailabilityFilterVisibility,
  ProductFinderAssortmentFilterSettings
} from "../../../commons/types/settings";

import { getFilteredModelYearsForBrand } from "./brand";

export interface FilterItemDisabledConfig {
  brand: string;
  years: number[];
}

export enum FilterItemDisabledType {
  InsufficientFilterOptions,
  InsufficientDataQuality,
  Offline
}

/**
 * Returns the effective visibility setting, gracefully falling back when the
 * configured setting doesn't match available data sources.
 *
 * This handles misconfiguration scenarios where the admin has set a visibility
 * mode that doesn't match what's actually configured:
 * - Setting is "both_*" but only WAWI data exists → returns "dealer_only"
 * - Setting is "both_*" but only Veloconnect data exists → returns "supplier_only"
 * - Setting matches available data → returns the original setting
 *
 * @example
 * // Admin set "both_default_dealer" but later removed Veloconnect integration
 * getGracefulAvailabilityVisibility("both_default_dealer", { hasWawiData: true, hasVeloconnectData: false }) // → "dealer_only"
 */
export const getGracefulAvailabilityVisibility = (
  setting: AvailabilityFilterVisibility,
  { hasWawiData, hasVeloconnectData }: { hasWawiData: boolean; hasVeloconnectData: boolean }
): AvailabilityFilterVisibility => {
  if (hasWawiData && !hasVeloconnectData) {
    return "dealer_only";
  }

  if (!hasWawiData && hasVeloconnectData) {
    return "supplier_only";
  }

  return setting;
};

type GetDefaultActiveFiltersOptions<Type extends ProductType> = {
  productFilterValues?: ProductFilterValues<Type>;
  showAllModelYears?: boolean;
  selectAssortmentAutomatically?: boolean;
  automaticAssortmentFilter?: AutomaticAssortmentFilter;
  availabilityFilterVisibility?: AvailabilityFilterVisibility;
};

export const getDefaultFilterValuesForCombinedAssortmentFilter = <Type extends ProductType>(
  automaticAssortmentFilter: AutomaticAssortmentFilter | undefined,
  possibleValues: ProductFilterValues<Type> | undefined,
  includeSupplierFilters: boolean
): CombinedAssortmentAvailabilityState[] => {
  if (!automaticAssortmentFilter) {
    return [];
  }

  const possibleFilterValues = (possibleValues?.[ProductSpecKey.CombinedAssortment] ?? []).map(
    filterValue => filterValue.value
  );

  return {
    [AutomaticAssortmentFilter.OnlyIfAvailable]: [
      `wawi_${WawiAssortmentAvailabilityState.InStock}`,
      `wawi_${WawiAssortmentAvailabilityState.InStockElsewhere}`,
      `wawi_${WawiAssortmentAvailabilityState.AvailableAtSupplier}`,
      ...(includeSupplierFilters ? [`veloconnect_${UpstreamItemStatus.AVAILABLE}`] : [])
    ] as CombinedAssortmentAvailabilityState[],

    [AutomaticAssortmentFilter.NotUnknown]: [
      ...Object.values(WawiAssortmentAvailabilityState).map(
        (state): PrefixedWawiAssortmentAvailabilityState => `wawi_${state}`
      ),
      ...Object.values(includeSupplierFilters ? UpstreamItemStatus : {}).map(
        (status): PrefixedUpstreamItemStatus => `veloconnect_${status}`
      )
    ].filter(
      status =>
        status !== `wawi_${WawiAssortmentAvailabilityState.Unknown}` &&
        status !== `veloconnect_${UpstreamItemStatus.NOT_FOUND}`
    ) as CombinedAssortmentAvailabilityState[],

    [AutomaticAssortmentFilter.AvailableOrOrdered]: [
      `wawi_${WawiAssortmentAvailabilityState.InStock}`,
      `wawi_${WawiAssortmentAvailabilityState.InStockElsewhere}`,
      `wawi_${WawiAssortmentAvailabilityState.AvailableAtSupplier}`,
      `wawi_${WawiAssortmentAvailabilityState.Ordered}`,
      ...(includeSupplierFilters
        ? [`veloconnect_${UpstreamItemStatus.AVAILABLE}`, `veloconnect_${UpstreamItemStatus.EXPECTING_DELIVERY}`]
        : [])
    ] as CombinedAssortmentAvailabilityState[]
  }[automaticAssortmentFilter].filter(state => possibleFilterValues.includes(state));
};

export const shouldIncludeSupplierFilters = (visibility: AvailabilityFilterVisibility): boolean =>
  visibility === "supplier_only" || visibility === "both_default_everywhere";

export const getDefaultActiveFilters = <Type extends ProductType>(
  assortmentType: AssortmentType | null,
  {
    productFilterValues = {},
    automaticAssortmentFilter = undefined,
    showAllModelYears = false,
    selectAssortmentAutomatically = true,
    availabilityFilterVisibility = "dealer_only"
  }: GetDefaultActiveFiltersOptions<Type> = {}
): ActiveFilters<Type> => {
  const activeFilters = {};

  const shouldModifyModelYears = shouldShowAllModelYears(showAllModelYears, selectAssortmentAutomatically);

  const activeFiltersWithModelYears: ActiveFilters<Type> = shouldModifyModelYears
    ? setActiveFilter(
        activeFilters,
        ProductSpecKey.ModelYear,
        (productFilterValues[ProductSpecKey.ModelYear] ?? []).map(({ value }) => value)
      )
    : activeFilters;

  if (selectAssortmentAutomatically) {
    if (assortmentType === AssortmentType.Manual) {
      const defaultFilterValue = ManualAssortmentAvailabilityState.Current as FilterValue<
        Type,
        ProductSpecKey.ManualAssortment
      >;

      return setActiveFilter(activeFiltersWithModelYears, ProductSpecKey.ManualAssortment, [defaultFilterValue]);
    } else if (assortmentType === AssortmentType.Automatic) {
      const defaultFilterValues = getDefaultFilterValuesForCombinedAssortmentFilter(
        automaticAssortmentFilter,
        productFilterValues,
        shouldIncludeSupplierFilters(availabilityFilterVisibility)
      ) as FilterValue<Type, ProductSpecKey.CombinedAssortment>;

      const activeFiltersWithCombinedAssortment = setActiveFilter(
        activeFiltersWithModelYears,
        ProductSpecKey.CombinedAssortment,
        [defaultFilterValues]
      );

      return activeFiltersWithCombinedAssortment;
    }
  }

  return activeFiltersWithModelYears;
};

export type GetEvaluatedDefaultActiveFilterOptions<Type extends ProductType> = {
  assortmentType: AssortmentType | null;
  assortmentFilterSettings: ProductFinderAssortmentFilterSettings;
  activeFilters: ActiveFilters<Type>;
  productFilterValues: ProductFilterValues<Type>;
  filterConfig: FilterConfig<Type>;
  automaticAssortmentFilter?: AutomaticAssortmentFilter;
};

export const getEvaluatedDefaultActiveFilters = <Type extends ProductType>({
  automaticAssortmentFilter,
  assortmentType,
  assortmentFilterSettings,
  activeFilters,
  productFilterValues,
  filterConfig
}: GetEvaluatedDefaultActiveFilterOptions<Type>): ActiveFilters<Type> => {
  const { showAllModelYears, selectAssortmentAutomatically, availabilityFilterVisibility } = assortmentFilterSettings;
  // Respect a manually disabled assortment filter by the user, when evaluating the default active filters
  // Note: An empty array is our indicator that a filter was active, and got cleared again
  const isAssortmentFilterManuallyDisabled =
    (assortmentType === AssortmentType.Manual &&
      getActiveFilterValues(activeFilters, ProductSpecKey.ManualAssortment)?.length === 0) ||
    (assortmentType === AssortmentType.Automatic &&
      getActiveFilterValues(activeFilters, ProductSpecKey.CombinedAssortment)?.length === 0);

  const shouldModifyAssortment: boolean = !isAssortmentFilterManuallyDisabled && selectAssortmentAutomatically;

  const possibleValues: ActiveFilters<Type> = mapValues(productFilterValues, values =>
    (values ?? []).map(({ value }) => value)
  );
  const possibleActiveFilters = mapValues(
    activeFilters,
    (values: FilterValue<Type, FilterKey<Type>>[], key: FilterKey<Type>) =>
      filterConfig[key]?.hasSyntheticValues
        ? getActiveFilterValues(activeFilters, key)
        : intersection(values, possibleValues[key])
  );
  const defaultActiveFilters = getDefaultActiveFilters(assortmentType, {
    automaticAssortmentFilter,
    showAllModelYears,
    productFilterValues,
    selectAssortmentAutomatically: shouldModifyAssortment,
    availabilityFilterVisibility
  });

  return {
    ...possibleActiveFilters,
    ...defaultActiveFilters
  } as ActiveFilters<Type>;
};

export const addActiveFilterValue = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  filterKey: K,
  filterValue: FilterValue<Type, K>
) =>
  ({
    ...(activeFilters ?? {}),
    [filterKey]: [...(getActiveFilterValues(activeFilters, filterKey) || []), filterValue]
  }) as ActiveFilters<Type>;

export const removeActiveFilterValue = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  filterKey: K,
  filterValue: FilterValue<Type, K>
) =>
  ({
    ...(activeFilters ?? {}),
    [filterKey]: (getActiveFilterValues(activeFilters, filterKey) || []).filter(
      currentValue => currentValue !== filterValue
    )
  }) as ActiveFilters<Type>;

export const setActiveFilter = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  filterKey: K,
  filterValues: FilterValue<Type, K>[]
) =>
  ({
    ...(activeFilters ?? {}),
    [filterKey]: filterValues
  }) as ActiveFilters<Type>;

export const setActiveFilters = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  newActiveFilters: {
    [key in K]: FilterValue<Type, K>[];
  }
) => ({ ...activeFilters, ...newActiveFilters }) as ActiveFilters<Type>;

export const clearActiveFilter = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  filterKey: K
): ActiveFilters<Type> => clearActiveFilters(activeFilters, [filterKey]);

export const clearActiveFilters = <Type extends ProductType>(
  activeFilters: ActiveFilters<Type>,
  filterKeys?: FilterKey<Type>[]
): ActiveFilters<Type> => {
  const keysToClear = filterKeys ? filterKeys : Object.keys(activeFilters ?? {});
  return omit(activeFilters, keysToClear);
};

export const clearAllActiveFilters = <Type extends ProductType>(
  activeFilters: ActiveFilters<Type>,
  settings: ProductFinderAssortmentFilterSettings
): ActiveFilters<Type> => {
  if (settings.hideAutomaticAssortmentFilter) {
    const filtersToKeep: FilterKey<Type>[] = [ProductSpecKey.CombinedAssortment];

    const keysToClear = activeFilters ? keys(activeFilters).filter(key => !filtersToKeep.includes(key)) : undefined;

    return clearActiveFilters(activeFilters, keysToClear);
  } else {
    return clearActiveFilters(activeFilters);
  }
};

export const isActiveFilter = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  filterKey: K,
  filterValue?: FilterValue<Type, K>
): boolean => {
  const hasMissingValueArg = isNil(filterValue);
  const activeFilterValue = getActiveFilterValues(activeFilters, filterKey);

  if (hasMissingValueArg) {
    return Array.isArray(activeFilterValue) ? activeFilterValue.length > 0 : !!activeFilterValue;
  }

  return (activeFilterValue ?? []).includes(filterValue);
};

export const countActiveFilters = <Type extends ProductType>(activeFilters: ActiveFilters<Type>): number =>
  keys(activeFilters ?? {}).filter(key => isActiveFilter(activeFilters, key)).length;

export const hasActiveFilters = <Type extends ProductType>(activeFilters: ActiveFilters<Type>): boolean =>
  countActiveFilters(activeFilters) > 0;

export const getActiveFilterValues = <Type extends ProductType, K extends FilterKey<Type>>(
  activeFilters: ActiveFilters<Type>,
  filterKey: K
) => {
  if (activeFilters && filterKey in activeFilters) {
    return flatten(activeFilters[filterKey]);
  }

  return undefined;
};

export const showsOnlyEBikes = <Type extends ProductType>(activeFilters: ActiveFilters<Type>): boolean => {
  const activeFilterValues = getActiveFilterValues<ProductType.Bicycle, BicycleSpecKey.BikeTypeKey>(
    activeFilters as ActiveFilters<ProductType.Bicycle>,
    BicycleSpecKey.BikeTypeKey
  );

  return activeFilterValues?.length === 1 && activeFilterValues.includes("E_BIKE" satisfies BicycleTypeKey) === true;
};

export const shouldShowAllModelYears = (showAllModelYears: boolean, selectAssortmentAutomatically: boolean) =>
  showAllModelYears && selectAssortmentAutomatically;

export const hasMatchingInsufficientFilter = <Type extends ProductType>(
  filterKey: FilterKey<Type>,
  insufficientKeys: FilterKey<Type>[] = []
): boolean => insufficientKeys.includes(filterKey);

const hasInsufficientFilterOptions = <Type extends ProductType, K extends FilterKey<Type>>(
  filterKey: K,
  filterConfigItem: FilterConfigItem<Type, K>,
  filterValues: ProductFilterValues<Type>
): boolean =>
  (filterConfigItem.type === "default" ||
    filterConfigItem.type === "range" ||
    filterKey === ProductSpecKey.Price ||
    filterKey === BicycleSpecKey.ForkSuspensionTravel) &&
  getFilterKeysForFilterItem<Type>(filterKey, filterConfigItem).every(key => (filterValues?.[key] ?? []).length < 1);

const isOfflineUnavailableFilter = <Type extends ProductType>(
  filterKey: FilterKey<Type>,
  isOffline: boolean
): boolean => filterKey === CustomBicycleFilterKey.Sizing && isOffline;

export const getFilterItemDisabledYearsByActiveFilters =
  <Type extends ProductType>(filterKeys: FilterKey<Type>[], insufficientFilterValues: InsufficientFilterValues<Type>) =>
  (acc: number[], year: number): number[] =>
    filterKeys.some(filterKey => hasMatchingInsufficientFilter(filterKey, insufficientFilterValues[year]))
      ? [...acc, year]
      : acc;

export const getFilterItemDisabledType = <Type extends ProductType, K extends FilterKey<Type>>(
  filterKey: K,
  filterConfigItem: FilterConfigItem<Type, K>,
  filterValues: ProductFilterValues<Type>,
  filterKeysWithInsufficientFilterValues: FilterKey<Type>[] = [],
  isOffline: boolean
): FilterItemDisabledType | null => {
  const hasMatchingInsufficientFilterValues = getFilterKeysForFilterItem<Type>(filterKey, filterConfigItem).some(key =>
    hasMatchingInsufficientFilter(key, filterKeysWithInsufficientFilterValues)
  );

  if (hasMatchingInsufficientFilterValues) {
    return FilterItemDisabledType.InsufficientDataQuality;
  } else if (hasInsufficientFilterOptions(filterKey, filterConfigItem, filterValues)) {
    return FilterItemDisabledType.InsufficientFilterOptions;
  } else if (isOfflineUnavailableFilter<Type>(filterKey, isOffline)) {
    return FilterItemDisabledType.Offline;
  } else {
    return null;
  }
};

export const getFilterItemDisabledConfig = <Type extends ProductType, K extends FilterKey<Type>>(
  brand: Brand<Type>,
  filterKey: K,
  filterConfigItem: FilterConfigItem<Type, K>,
  activeFilters: ActiveFilters<Type>
): FilterItemDisabledConfig => {
  const insufficientFilterValues = brand.insufficientFilterValues ?? {};
  const filterKeys = getFilterKeysForFilterItem<Type>(filterKey, filterConfigItem);

  const filteredModelYearsForBrand = getFilteredModelYearsForBrand<Type>(brand, activeFilters ?? {});

  return {
    brand: brand.displayName,
    years: filteredModelYearsForBrand.reduce(
      getFilterItemDisabledYearsByActiveFilters(filterKeys, insufficientFilterValues),
      []
    )
  };
};

/**
 * Either returns a reason why it was disabled, or null.
 * This indicates if the item should be disabled or not.
 */
export const getFilterItemDisabledSettings = <Type extends ProductType, K extends FilterKey<Type>>(
  filterKey: K,
  filterConfigItem: FilterConfigItem<Type, K>,
  filterValues: ProductFilterValues<Type>,
  brands: Brand<Type>[],
  activeFilters: ActiveFilters<Type>,
  filterKeysWithInsufficientFilterValues: FilterKey<Type>[] = [],
  isOffline: boolean
): { filterItemDisabledType: FilterItemDisabledType | null; disabledConfigs: FilterItemDisabledConfig[] } => {
  const filterItemDisabledType = getFilterItemDisabledType(
    filterKey,
    filterConfigItem,
    filterValues,
    filterKeysWithInsufficientFilterValues,
    isOffline
  );

  if (filterItemDisabledType === null) {
    return { filterItemDisabledType: null, disabledConfigs: [] };
  } else {
    const disabledConfigs = brands.map(brand =>
      getFilterItemDisabledConfig(brand, filterKey, filterConfigItem, activeFilters)
    );

    return { filterItemDisabledType, disabledConfigs };
  }
};

export const isFilterItemActive = <Type extends ProductType, K extends FilterKey<Type> = FilterKey<Type>>(
  filterKey: K,
  filterConfigItem: FilterConfigItem<Type, FilterKey<Type>>,
  activeFilters: ActiveFilters<Type>
): number => {
  const activeCount = getFilterKeysForFilterItem<Type>(filterKey, filterConfigItem).reduce(
    (count, key) => count + (getActiveFilterValues(activeFilters, key)?.length ?? 0),
    0
  );
  const isActive = activeCount > 0;

  const isActiveSizingType = filterKey === CustomBicycleFilterKey.Sizing && isActive;
  const isActiveRangeType = filterConfigItem.type === "range" && isActive;
  const isActiveForkSuspensionTravelType = filterKey === BicycleSpecKey.ForkSuspensionTravel && isActive;

  const shouldActiveCountBeOne = isActiveRangeType || isActiveForkSuspensionTravelType || isActiveSizingType;

  return shouldActiveCountBeOne ? 1 : activeCount;
};

export const getProductFiltersFromActiveFilters = <Type extends ProductType>(
  activeFilters: ActiveFilters<Type>,
  filterConfig: FilterConfig<Type>,
  fallbackGetActiveFiltersFn?: (key: FilterKey<Type>) => (values: any[]) => ProductFilter[]
): ProductFilter[] => {
  const activeFilterEntries = Object.entries(activeFilters ?? {});

  return activeFilterEntries
    .flatMap(([key, values]) => {
      const filterKey = key as FilterKey<Type>;
      const filterConfigItem = filterConfig[filterKey];

      return filterConfigItem
        ? filterConfigItem.getActiveFilters(values)
        : fallbackGetActiveFiltersFn?.(filterKey)(values);
    })
    .filter(byDefined);
};

/**
 * Returns true if `now` is on or before end-of-day (local time) of the
 * given ISO date (`YYYY-MM-DD`). Returns false on invalid dates.
 */
const isBeforeOrOnIsoDate = (isoDate: string, now: Date): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return false;
  }

  const [, yearString, monthString, dayString] = match;
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);

  const dateIsValid =
    endOfDay.getFullYear() === year && endOfDay.getMonth() === month - 1 && endOfDay.getDate() === day;

  return dateIsValid && now.getTime() <= endOfDay.getTime();
};

/**
 * Whether the filter row should display a "NEW" badge based on its
 * `newUntil` config value (inclusive, end-of-day).
 */
export const isFilterMarkedNew = (config: FilterConfigMeta | undefined, now: Date = new Date()): boolean => {
  const newUntil = config?.newUntil;
  return typeof newUntil === "string" && isBeforeOrOnIsoDate(newUntil, now);
};
